import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Box, FormControl, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { UserDocumentTypes } from "../../../../constants/userDocuments";
import { BecomeProService } from '../../../../services/becomeProService';
import { DocumentUploadSection } from '../../../../shared/components/documentUploadSection';
import { ButtonWrapper } from "../../../../shared/components/formUtilities/useButton";
import { TextWrapper } from "../../../../shared/components/formUtilities/useTextWrapper";

const flowSteps = {
  init: 1,
  regulatoryQn: 2,
  uploadNetWorth: 3,
  reject: 4,
  declaration: 5
};

export const KSABecomePro = ({ onSuccess = null }) => {
  const becomeProService = new BecomeProService();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const [answers, setAnswers] = useState([
    { id: 1, value: null, question: t('annualIncome600000'), isAnswered: false },
    { id: 2, value: null, question: t('min3YearsExp'), isAnswered: false },
    { id: 3, value: null, question: t('holdingRecognizedFinance'), isAnswered: false },
    { id: 4, value: null, question: t('netAssetsof3Years'), isAnswered: false }
  ]);
  const [netWorthProof, setNetWorthProof] = useState(null);


  const renderInitSection = () => {
    return <Box className='flex flex-col items-center text-center'>
      <TextWrapper
        classList={`!text-[18px] !font-[600] `}
        text={t('qAClientClassification')}></TextWrapper>
      <TextWrapper
        type="caption"
        text={t('qAClientClassificationDesc')}></TextWrapper>
    </Box>;
  };

  const getRegulatoryQuestions = (data) => {
    return <Box className='mt-2 flex flex-col'>
      <TextWrapper
        classList={`!text-[16px] !text-start`}
        text={t(data.question)}></TextWrapper>
      <FormControl className="!mt-3 flex !px-1">
        <RadioGroup className='flex !flex-row'
          value={data?.value}
          onChange={(e) => {
            const isChecked = e.target.value === 'true';
            setAnswers(prevAnswers =>
              prevAnswers.map(answer =>
                answer.id === data?.id ? { ...answer, value: isChecked, isAnswered: true } : answer
              )
            );
          }}>
          {[{ name: 'yes', value: true, }, { name: 'no', value: false }].map((resp, index) => {
            return (
              <FormControlLabel
                key={index}
                value={resp?.value}
                control={<Radio className='!px-[3px] !pb-[3px] !pt-0' />}
                label={t(resp?.name)}
              />
            );
          })}
        </RadioGroup>
      </FormControl>
    </Box>;
  };

  const renderRegulatorySection = () => {
    return <Box className='flex flex-col items-start text-center space-y-4'>
      <TextWrapper
        classList={`!text-[18px] !font-[600] `}
        text={t('toQualityAsAprofessionalInvestor')}></TextWrapper>
      {answers.map((resp, index) => {
        return <Box key={resp.id}>{getRegulatoryQuestions(resp)}</Box>;
      })}
    </Box>;
  };

  const renderNetWorthUpload = () => {
    return <Box className='flex flex-col items-center text-center'>
      <TextWrapper
        classList={`!text-[18px] !font-[600] `}
        text={t('qAPleaseProvideDocumentary')}></TextWrapper>
      <TextWrapper
        type="caption"
        text={t('qAPleaseProvideDocumentaryDesc')}></TextWrapper>
      <Box className="flex flex-row items-start !text-start justify-start mt-8">
        <DocumentUploadSection
          subPoints={[
            'dragProofofProofOfEligibilityPoint1',
            'dragProofofProofOfEligibilityPoint2',
            'dragProofofProofOfEligibilityPoint3',
            'dragProofofProofOfEligibilityPoint4',
          ]}
          onRemoveFile={(e) => {
            setNetWorthProof(null);
          }}
          onUploadedFiles={(e) => {
            setNetWorthProof(e?.file);
          }}
          fileName="dragProofofProofOfEligibility"
          file={netWorthProof}
          isLoading={loading}
          fileType={
            UserDocumentTypes.InvestorProofOfEligibility
          }></DocumentUploadSection>
      </Box>
    </Box>;
  };

  const renderRejected = () => {
    return <Box className='flex items-center justify-center'>
      <TextWrapper
        classList={`!text-[18px] !font-[600] `}
        text={t('qaSorryWeCant')}></TextWrapper>
    </Box>;
  };

  const renderDeclaration = () => {
    return <Box className="grid grid-flow-row gap-4">
      {[1, 2, 3, 4].map((resp) => {
        return (
          <Box
            key={resp}
            className="flex flex-0 items-start">
            <CheckCircleOutlineIcon
              className="!text-[20px] !mt-1"
              color="primary"></CheckCircleOutlineIcon>
            <TextWrapper
              classList="!text-[16px] !font-[500] !mx-2"
              text={t(`qAConfirmation${resp}`)}></TextWrapper>
          </Box>
        );
      })}
    </Box>;
  };

  const renderItems = () => {
    switch (step) {
      case flowSteps.init: {
        return renderInitSection();
      } case flowSteps.regulatoryQn: {
        return renderRegulatorySection();
      } case flowSteps.uploadNetWorth: {
        return renderNetWorthUpload();
      } case flowSteps.reject: {
        return renderRejected();
      } case flowSteps.declaration: {
        return renderDeclaration();
      }
      default: {
        return renderInitSection();
      }
    }
  };

  const onBack = () => {
    if (step === flowSteps.regulatoryQn) {
      setStep(flowSteps.init);
    } else if (step === flowSteps.reject) {
      setStep(flowSteps.init);
    } else if (step === flowSteps.uploadNetWorth) {
      setStep(flowSteps.regulatoryQn);
    } else if (step === flowSteps.declaration) {
      setStep(flowSteps.uploadNetWorth);
    }
  };

  const onNext = () => {
    if (step === flowSteps.init) {
      setStep(flowSteps.regulatoryQn);
    } else if (step === flowSteps.regulatoryQn) {
      if (answers.every(resp => !resp.value)) {
        setStep(flowSteps.reject);
        return;
      }
      setStep(flowSteps.uploadNetWorth);
    } else if (step === flowSteps.uploadNetWorth) {
      setStep(flowSteps.declaration);
    } else if (step === flowSteps.declaration) {
      onSubmit();
    }
  };

  const onSubmit = () => {
    let becomePro = {
      netWorthProofUrl: netWorthProof?.file?.url,
      questionnaire: answers.map(({ question, value }) => ({ question, answer: value }))
    };
    setLoading(true);
    becomeProService
      .updateQuestionAnswers(becomePro)
      .then((resp) => {
        setLoading(false);
        onSuccess();
      })
      .catch((err) => {
        setLoading(false);
      });
  };
  const isNexbtnDisabled = () => {
    if (step === flowSteps.regulatoryQn && answers.some(res => !res.isAnswered)) {
      return true;
    } else if (step === flowSteps.uploadNetWorth && !netWorthProof?.file?.url) {
      return true;
    }
    return false;
  };

  return <Box className='flex flex-col items-center'>
    <Box className="md:w-[600px] grid grid-flow-row gap-4">
      <Box className="grid grid-flow-row gap-4">
        {renderItems()}
      </Box>
    </Box>
    <Box className="flex flex-0 justify-center items-center mt-6">
      <ButtonWrapper
        disabled={loading}
        hideBtn={step === flowSteps.init}
        classList="!mx-2 !min-w-[120px]"
        label={t('back')}
        onClick={() => { onBack(); }}
        variant={'outlined'}
      ></ButtonWrapper>
      <ButtonWrapper
        hideBtn={step === flowSteps.reject}
        loading={loading && step === flowSteps.declaration}
        disabled={isNexbtnDisabled()}
        classList="!min-w-[120px] !shadow-none"
        onClick={() => { onNext(); }}
        label={
          step === flowSteps.declaration ? t('submit') : t('next')
        }></ButtonWrapper>
    </Box>
  </Box>;
};